import { useMemo, useState } from 'react';

import {
  Box,
  Container,
  FAQ,
  HeadCellSortState,
  Link,
  Pagination,
  ParsedHtmlContent,
  theme,
  toggleSortStates,
  useMediaMin,
  usePagination,
} from '@foradevelopment/fora-ui';

import { IntroText } from '../../components';
import { useAppSelector } from '../../redux/hooks';
import { InvoicesTable } from '../components/InvoicesTable/InvoicesTable';
import {
  defaultInvoicesListSortState,
  InvoicesTableSortIdKeys,
  InvoicesTableSortState,
} from '../components/InvoicesTable/InvoicesListSortState';
import { sortInvoicesListIndices } from '../../common/utils/invoicesSorting';
import { links } from '../../router/links';
import { useTypedTranslation } from '../../i18n/i18n';

const itemsPerPage = 25;

export const InvoicesView = () => {
  const invoiceList = useAppSelector(state => state.invoice.invoices);
  const faqs = useAppSelector(state => state.content.content.faqListCostAndInvoice);
  const ingress = useAppSelector(state => state.content.content.ingressCostAndInvoice);
  const isDesktop = useMediaMin(theme.breakpoint.lg);
  const { t } = useTypedTranslation();

  const [tableSortState, setTableSortState] = useState<InvoicesTableSortState>({
    ...defaultInvoicesListSortState,
    ocrNumber: HeadCellSortState.Desc,
  });

  const sortedInvoicesListIndices = useMemo(() => {
    const invoiceListIndices = invoiceList.map((_, ind) => ind);
    return sortInvoicesListIndices({
      invoicesList: invoiceList,
      sortState: tableSortState,
      indices: invoiceListIndices,
    });
  }, [invoiceList, tableSortState]);

  const sortTable = (sortId: InvoicesTableSortIdKeys) =>
    setTableSortState(state => {
      // Reset from initial state to make sure that only one column is sorted at a time
      const newState = { ...defaultInvoicesListSortState };
      newState[sortId] = toggleSortStates(state[sortId]);
      return newState;
    });

  const { paginationProps, paginatedListIndices } = usePagination(sortedInvoicesListIndices, itemsPerPage);

  // Can be used to display a reminder (with the element below)
  // const showAlert = invoiceList.some(
  //   e => e.status === InvoiceStatusEnum.Reminded && t(tKeys.invoice.startPage.alert.reminded)
  // );

  return (
    <>
      <IntroText>
        <Box maxWidth={778} marginBottom={20}>
          <ParsedHtmlContent htmlContent={ingress?.body ?? ''} styling={{ fontSize: 'large' }} />
        </Box>
        <Container>
          <Link fontSize={'medium'} to={links.invoicesHelp}>
            {t('section.invoiceOverview.helpPageLink')}
          </Link>
        </Container>
      </IntroText>

      {/* {showAlert && (
        <Box maxWidth={783} marginBottom={60}>
          <Alert variant="warning" size="wide" paddingLeft={[20, 20, 40]} paddingRight={[20, 40, 40]}>
            <Text size="small">{t(tKeys.invoice.startPage.alert.reminded)}</Text>
          </Alert>
        </Box>
      )} */}
      {invoiceList && (
        <>
          <InvoicesTable
            invoices={invoiceList}
            invoicesListIndices={paginatedListIndices}
            sortState={tableSortState}
            sortTable={sortTable}
          />
          <Pagination {...paginationProps} />
        </>
      )}
      {!!faqs.length && (
        <FAQ testId="review-confirm-faq" layout={isDesktop ? 'drawer' : 'modal'} questionAnswers={faqs} />
      )}
    </>
  );
};
